import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getInvoiceDates, getAgencies } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SearchableSelect } from '../components/ui/searchable-select';
import { FileText, CalendarBlank, DownloadSimple, Receipt, CurrencyInr } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

export default function InvoicesPage() {
  const { user } = useAuth();
  const [dates, setDates] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agencyFilter, setAgencyFilter] = useState('');
  const [downloading, setDownloading] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant';
  const isDistributor = user?.role === 'distributor';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (agencyFilter) params.agency_id = agencyFilter;
      const [datesRes, agRes] = await Promise.all([
        getInvoiceDates(params),
        isAdmin ? getAgencies().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setDates(datesRes.data.dates || []);
      setAgencies(agRes.data.filter(a => a.status === 'active'));
    } catch (err) {
      toast.error('Data load failed');
    } finally {
      setLoading(false);
    }
  }, [agencyFilter, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadInvoice = async (type, params, filename) => {
    const key = `${type}-${JSON.stringify(params)}`;
    setDownloading(key);
    try {
      const queryStr = new URLSearchParams(params).toString();
      // Use relative path for same-origin, full URL for cross-origin
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/api/invoices/${type}?${queryStr}`;

      const pdf = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.withCredentials = true;
        xhr.timeout = 30000;
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(xhr.response);
          } else {
            // Try to read error message from response
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const err = JSON.parse(reader.result);
                reject(new Error(err.detail || `Error ${xhr.status}`));
              } catch (e) {
                reject(new Error(`Download failed (${xhr.status})`));
              }
            };
            reader.onerror = () => reject(new Error(`Download failed (${xhr.status})`));
            reader.readAsText(xhr.response);
          }
        };
        xhr.onerror = () => reject(new Error('Server se connect nahi ho paya'));
        xhr.ontimeout = () => reject(new Error('Request timeout — try again'));
        xhr.send();
      });

      const a = document.createElement('a');
      a.href = URL.createObjectURL(pdf);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success(`${filename} downloaded`);
    } catch (err) {
      toast.error(err.message || 'Invoice download failed');
    } finally {
      setDownloading(null);
    }
  };

  // Group dates by month
  const monthGroups = {};
  dates.forEach(d => {
    const parts = d.date?.split('/') || d.date?.split('-') || [];
    let monthKey;
    if (parts.length === 3) {
      // dd/mm/yyyy or yyyy-mm-dd
      monthKey = parts[0].length === 4 ? `${parts[0]}-${parts[1]}` : `${parts[2]}-${parts[1]}`;
    } else {
      monthKey = 'unknown';
    }
    if (!monthGroups[monthKey]) monthGroups[monthKey] = { dates: [], totalBookings: 0, totalAmount: 0 };
    monthGroups[monthKey].dates.push(d);
    monthGroups[monthKey].totalBookings += d.bookings;
    monthGroups[monthKey].totalAmount += d.total;
  });

  const monthNames = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  if (!isAdmin && !isDistributor) return <div className="p-4 text-red-600">Access denied.</div>;

  return (
    <div className="space-y-5" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="invoices-title">Invoices</h1>
          <p className="text-sm text-gray-500 mt-0.5">GST Tax Invoice download karo — Daily ya Monthly</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border rounded-lg px-3 py-2">
          <Receipt size={16} className="text-[#002FA7]" />
          <span>GSTIN: <strong>10AAYCA0050H1Z1</strong></span>
        </div>
      </div>

      {/* Agency Filter (admin only) */}
      {isAdmin && agencies.length > 0 && (
        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-3" data-testid="invoice-agency-filter">
          <Label className="text-sm whitespace-nowrap">Agency:</Label>
          <SearchableSelect
            options={[{ value: '', label: 'All Agencies' }, ...agencies.map(a => ({ value: a.id, label: a.name }))]}
            value={agencyFilter}
            onValueChange={setAgencyFilter}
            placeholder="All Agencies"
          />
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : dates.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText size={48} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Koi confirmed booking nahi mili</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(monthGroups).sort(([a], [b]) => b.localeCompare(a)).map(([monthKey, group]) => {
            const parts = monthKey.split('-');
            const monthLabel = parts.length === 2 ? `${monthNames[parseInt(parts[1])] || parts[1]} ${parts[0]}` : monthKey;

            return (
              <div key={monthKey} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`month-${monthKey}`}>
                {/* Month Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                  <div className="flex items-center gap-2">
                    <CalendarBlank size={18} className="text-[#002FA7]" />
                    <h2 className="font-semibold text-gray-900 text-sm">{monthLabel}</h2>
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">{group.dates.length} days</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">{group.totalBookings} bookings &middot; {fmt(group.totalAmount)}</span>
                    <Button size="sm" className="bg-[#002FA7] hover:bg-[#002590] text-xs h-7 gap-1"
                      disabled={downloading === `monthly-${monthKey}`}
                      onClick={() => downloadInvoice('monthly', { month: monthKey, agency_id: agencyFilter }, `Invoice_${monthLabel.replace(' ', '_')}.pdf`)}
                      data-testid={`download-monthly-${monthKey}`}>
                      <DownloadSimple size={14} /> Monthly Invoice
                    </Button>
                  </div>
                </div>

                {/* Daily rows */}
                <Table className="table-dense">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Bookings</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Invoice</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.dates.map(d => (
                      <TableRow key={d.date} data-testid={`inv-row-${d.date}`}>
                        <TableCell className="text-sm font-medium">{d.date}</TableCell>
                        <TableCell className="text-right text-sm">{d.bookings}</TableCell>
                        <TableCell className="text-right text-sm font-medium">{fmt(d.total)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="text-[#002FA7] hover:text-blue-800 text-xs h-7 gap-1"
                            disabled={downloading === `daily-${d.date}`}
                            onClick={() => downloadInvoice('daily', { date: d.date, agency_id: agencyFilter }, `Invoice_${d.date.replace(/\//g, '-')}.pdf`)}
                            data-testid={`download-daily-${d.date}`}>
                            <DownloadSimple size={14} /> PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
